import clsx from 'clsx';
import useTranslation from 'next-translate/useTranslation';
import dayjs from 'dayjs';
import Trans from 'next-translate/Trans';
import Link from 'next/link';

// Components
import Tooltip from '@/components/base/Tooltip';
import AvatarCompound, { DefaultAvatar as Avatar } from '@/components/base/Avatar';
import Icon from '@/components/base/Icon';

// Hooks
import useUserRole from '@/utils/hooks/user/useUserRole';
import type { SpGroupType } from '@/utils/hooks/swr/group/useSPGroups';

// Libs
import { STATUS, makeDisplayedStatus } from '@/lib/socialprofiles/status';
import { DATA_ACCESS, SOCIAL_PROFILES } from '@/lib/routes/privateRoutes';

// Types
import type { DefaultAvatarProps } from '@/components/base/Avatar';
import type { ReactElement } from 'react';
import type { SocialProfileType } from '@/types/socialProfileType';

type AvatarMenuPropsType = DefaultAvatarProps & {
    hasHover?: boolean;
    onClick?: () => void;
    data: SocialProfileType | SpGroupType;
    variant: 'social-profile' | 'group';
    compact?: boolean;
    additionalInfosSize?: 'base' | 'small';
};
export function AvatarMenu({
    onClick,
    variant,
    data,
    compact = true,
    hasHover,
    additionalInfosSize = 'base',
    ...restProps
}: AvatarMenuPropsType) {
    if (!data) {
        return null;
    }

    return (
        <div className="relative flex items-center" data-testid="avatar-menu-wrapper">
            {variant !== 'group' && 'slug' in data ? (
                <AvatarMenuTooltip data={data} compact={compact}>
                    {typeof onClick === 'function' ? (
                        <button
                            data-testid="avatar-social-profile-menu-button"
                            onClick={onClick}
                            className="absolute left-0 top-0 h-11.5 w-11.5 overflow-hidden rounded-full focus:ring-offset-0"
                        />
                    ) : (
                        <span
                            data-testid="tooltip-ref"
                            className="absolute left-0 top-0 block h-11.5 w-11.5 cursor-pointer overflow-hidden rounded-full focus:ring-offset-0"
                        />
                    )}
                </AvatarMenuTooltip>
            ) : null}

            <AvatarDisplayed
                data={data}
                variant={variant}
                onClick={onClick}
                compact={compact}
                hasHover={hasHover}
                additionalInfosSize={additionalInfosSize}
                {...restProps}
            />
        </div>
    );
}

function TooltipContent({ content }: { content: string }) {
    return (
        <div className="flex max-w-[200px] overflow-hidden">
            <span className="block truncate">{content}</span>
        </div>
    );
}

const AvatarMenuTooltip = ({
    data,
    compact,
    children
}: {
    data: SocialProfileType;
    compact: boolean;
    children: ReactElement;
}) => {
    const status = makeDisplayedStatus(data);
    const errorTooltip = status === STATUS.disconnected || status === STATUS.reAuth;
    const warningTooltip = status === STATUS.refresh;
    const { isAdmin } = useUserRole();

    function getTooltipStatus() {
        if (status === STATUS.reAuth) {
            if (isAdmin) {
                return `commons:social_profile_error.reAuth_admin`;
            }
            return `commons:social_profile_error.reAuth_other`;
        }

        if (
            (data.social_network_type === 'instagram' || data.social_network_type === 'facebook') &&
            status === STATUS.disconnected
        ) {
            return 'commons:social_profile_error.disconnected';
        }

        // for any other status return fallback
        return 'commons:social_profile_error.refresh_profile';
    }

    function getHref() {
        //data access page is useful only for Instagram and Facebook when the account is disconnected
        if (
            (data.social_network_type === 'instagram' || data.social_network_type === 'facebook') &&
            status === STATUS.disconnected
        ) {
            return DATA_ACCESS;
        }
        return SOCIAL_PROFILES;
    }

    if ('slug' in data) {
        if (errorTooltip || warningTooltip) {
            return (
                <Tooltip
                    position="left"
                    interactive={true}
                    appendTo={document.body}
                    content={
                        <p className="block w-64">
                            <Trans
                                i18nKey={getTooltipStatus()}
                                components={{
                                    bold: <span className="font-GBold"></span>,
                                    link: (
                                        <Link href={getHref()} className="text-icoBlue underline" />
                                    )
                                }}
                                values={{
                                    slug: data.slug,
                                    count: Math.abs(
                                        dayjs().diff(
                                            dayjs.unix(data?.auth_status?.params?.last_token),
                                            'days'
                                        )
                                    )
                                }}
                            />
                        </p>
                    }
                    arrow={false}
                    variant="avatarError">
                    {children}
                </Tooltip>
            );
        }

        if (compact) {
            return (
                <Tooltip
                    position="left"
                    content={<TooltipContent content={data.slug} />}
                    arrow={false}
                    variant="avatar">
                    {children}
                </Tooltip>
            );
        }
    }

    return null;
};

export function AvatarDisplayed({
    data,
    variant,
    onClick,
    compact,
    hasHover,
    additionalInfosSize,
    ...restProps
}: AvatarMenuPropsType) {
    const { t } = useTranslation('commons');
    if (variant === 'social-profile') {
        const bindData = data as SocialProfileType;
        return (
            <div className="flex items-center justify-center gap-x-3">
                <Avatar
                    className="pointer-events-none"
                    fallback={bindData.slug[0]}
                    url={bindData?.external_api_data?.avatar}
                    profileType={bindData.social_network_type}
                    error={bindData?.auth_status?.type === 'error'}
                    warning={bindData?.auth_status?.type === 'warning'}
                    {...restProps}
                />

                {!compact ? (
                    <div
                        data-testid="additional-infos-social-profile"
                        className={clsx(
                            'flex flex-col justify-center overflow-hidden whitespace-nowrap text-left',
                            {
                                'w-48': additionalInfosSize === 'base',
                                'w-20': additionalInfosSize === 'small'
                            }
                        )}>
                        <p className="truncate font-GSemiBold text-sm">{bindData.slug}</p>
                        {bindData.external_api_data.name ? (
                            <p
                                className="truncate text-xs text-icoGray-500"
                                data-testid="social-profile-name">
                                {bindData.external_api_data.name}
                            </p>
                        ) : null}
                    </div>
                ) : null}
            </div>
        );
    }

    if (variant === 'group') {
        const bindData = data as SpGroupType;
        return (
            <div className="flex items-center justify-center gap-x-3">
                <Avatar
                    radius="rounded-md"
                    fallback={bindData.name.substring(0, 2)}
                    url={bindData.image}
                    customColorClass={bindData.fallback_style}
                    onHover={
                        hasHover && typeof onClick === 'function' ? (
                            <AvatarCompound.HoverButton
                                data-testid="avatar-group-menu-button"
                                onClick={onClick}
                                className="backdrop-blur-6">
                                <Tooltip
                                    position="left"
                                    content={<TooltipContent content={bindData.name} />}
                                    arrow={false}
                                    variant="avatar">
                                    <div className="flex h-full flex-1 items-center justify-center">
                                        <Icon icon="chevron-right" className="text-white" />
                                    </div>
                                </Tooltip>
                            </AvatarCompound.HoverButton>
                        ) : null
                    }
                    {...restProps}
                />

                {!compact ? (
                    <div
                        data-testid="additional-infos-group"
                        className={clsx(
                            'flex flex-col justify-center overflow-hidden whitespace-nowrap text-left',
                            {
                                'w-48': additionalInfosSize === 'base',
                                'w-32': additionalInfosSize === 'small'
                            }
                        )}>
                        <p className="truncate font-GSemiBold text-sm">{bindData.name}</p>
                        <span className="text-xs text-icoGray-500 dark:text-icoDarkMode-wolf">
                            {t('social_profiles_count', {
                                count: bindData.social_profiles?.filter(
                                    (social_profile) => social_profile.active
                                ).length
                            })}
                        </span>
                    </div>
                ) : null}
            </div>
        );
    }

    return null;
}

AvatarMenu.AvatarDisplayed = AvatarDisplayed;
